import { useCallback, useState, useMemo } from "react";
import {
  useBuildingPersistence,
  type Building,
} from "./useBuildingPersistence";
import type { PlacedObject, GridCell } from "../config/types";
import { useAuthStore } from "@/stores/authStore";
import useSound from "use-sound";
import type { FilterMode } from "@/stores/settingsStore";

type ModalMode = "create" | "edit" | "delete" | null;

interface PendingAction {
  mode: "create" | "edit" | "delete";
  position?: [number, number, number];
  buildingId?: string;
}

interface UseGridInteractionOptions {
  filterMode?: FilterMode;
  filterUserId?: string | null;
  restrictedCells?: Set<string>; // 건물 생성/렌더링 불가 영역
}

/**
 * 그리드 인터랙션 훅
 * 건물 배치, 선택, 수정, 삭제 관리
 */
export function useGridInteraction(
  showOnlyMyBuildings: boolean = false,
  options: UseGridInteractionOptions = {}
) {
  const [hoveredCell, setHoveredCell] = useState<GridCell | null>(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
    null
  );
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const { buildings, isLoading, createBuilding, deleteBuilding, loadBuildings } =
    useBuildingPersistence();

  const currentUserId = useAuthStore((state) => state.user?.id);

  // 클릭 사운드
  const [playClick] = useSound(import.meta.env.BASE_URL + "/sound/click.wav", {
    volume: 0.5,
  });

  // Building -> PlacedObject 변환 (필터링 적용)
  const placedObjects: PlacedObject[] = useMemo(() => {
    let filteredBuildings = buildings;

    // 새로운 필터 모드 적용
    const filterMode = options.filterMode || (showOnlyMyBuildings ? "mine" : "all");
    const filterUserId = options.filterUserId;
    const restrictedCells = options.restrictedCells;

    if (filterMode === "mine" && currentUserId) {
      filteredBuildings = buildings.filter((b) => b.userId === currentUserId);
    } else if (filterMode === "user" && filterUserId) {
      filteredBuildings = buildings.filter((b) => b.userId === filterUserId);
    }

    // 제한 영역에 있는 건물 필터링 (렌더링에서 제외)
    if (restrictedCells && restrictedCells.size > 0) {
      filteredBuildings = filteredBuildings.filter((b) => {
        const cellKey = `${b.dbPosition[0]},${b.dbPosition[2]}`;
        return !restrictedCells.has(cellKey);
      });
    }

    return filteredBuildings.map((b) => ({
      id: b.id,
      position: b.position,
      rotation: [0, 0, 0] as [number, number, number],
      scale: [1, 1, 1] as [number, number, number],
      meshIndex: b.meshIndex,
      buildingStructure: b.buildingStructure,
      buildingText: b.buildingText,
      title: b.title,
      userId: b.userId,
      username: b.username,
      userColor: b.userColor,
      tenants: b.tenants || [],
    }));
  }, [buildings, showOnlyMyBuildings, currentUserId, options.filterMode, options.filterUserId, options.restrictedCells]);

  // 셀 좌표로 건물 찾기 (DB 좌표 기준으로 비교)
  const findBuildingByCell = useCallback(
    (x: number, z: number): Building | undefined => {
      return buildings.find((b) => {
        // dbPosition은 정수형 셀 좌표 (0, 5, 10...)
        return b.dbPosition[0] === x && b.dbPosition[2] === z;
      });
    },
    [buildings]
  );

  // 그리드 셀 호버
  const onCellPointerOver = useCallback((x: number, z: number) => {
    setHoveredCell({ x, z });
  }, []);

  const onCellPointerOut = useCallback(() => {
    setHoveredCell(null);
  }, []);

  // 그리드 클릭 (빈 셀 + 건물 통합)
  // 실시간 커서 위치(hoveredCell) 기반으로 동작
  const onCellClick = useCallback(
    (
      onBuildingNavigate?: (buildingId: string) => void,
      onEmptyCellClick?: (x: number, z: number) => void
    ) => {
      // hoveredCell이 없으면 클릭 무시 (커서가 그리드 위에 없음)
      if (!hoveredCell) {
        console.log("[onCellClick] No hovered cell");
        return;
      }

      const { x, z } = hoveredCell;
      const existing = findBuildingByCell(x, z);

      if (existing) {
        // 기존 건물 클릭 -> 상세 페이지로 이동
        console.log(
          "[onCellClick] Building found, navigating to:",
          existing.id
        );
        playClick();
        if (onBuildingNavigate) {
          onBuildingNavigate(existing.id);
        }
      } else {
        // 빈 셀 클릭 -> 건물 에디터 페이지로 이동
        // 익명 사용자 체크는 페이지에서 처리
        console.log("[onCellClick] Empty cell, navigating to editor at:", x, z);
        playClick();

        // 에디터 페이지로 이동 콜백 호출
        if (onEmptyCellClick) {
          onEmptyCellClick(x, z);
        }
      }
    },
    [hoveredCell, findBuildingByCell, playClick]
  );

  // 건물 생성 확인
  const confirmCreate = useCallback(
    async (data: {
      title: string;
      buildingText: string;
      meshIndex: number;
    }) => {
      if (!pendingAction?.position) return;

      // 익명 사용자 체크
      const isAnonymous = useAuthStore.getState().isAnonymous();
      if (isAnonymous) {
        setError("방문자는 건물을 생성할 수 없습니다. 로그인이 필요합니다.");
        return;
      }

      const [x, y, z] = pendingAction.position;

      const result = await createBuilding(
        {
          position_x: x,
          position_y: y,
          position_z: z,
          mesh_index: data.meshIndex,
          building_text: data.buildingText.slice(0, 50), // 50자 제한
          title: data.title,
        },
        data.meshIndex
      );

      if (result) {
        setModalMode(null);
        setPendingAction(null);
      } else {
        setError("건물 생성에 실패했습니다.");
      }
    },
    [pendingAction, createBuilding]
  );

  // 건물 삭제 확인
  const confirmDelete = useCallback(async () => {
    if (!selectedBuildingId) return;

    // 익명 사용자 체크
    const isAnonymous = useAuthStore.getState().isAnonymous();
    if (isAnonymous) {
      setError("방문자는 건물을 삭제할 수 없습니다. 로그인이 필요합니다.");
      return;
    }

    const success = await deleteBuilding(selectedBuildingId);

    if (success) {
      setSelectedBuildingId(null);
      setModalMode(null);
    } else {
      setError("건물 삭제에 실패했습니다.");
    }
  }, [selectedBuildingId, deleteBuilding]);

  // 모달 닫기
  const closeModal = useCallback(() => {
    setModalMode(null);
    setPendingAction(null);
    setError(null);
  }, []);

  // 선택된 건물 정보
  const selectedBuilding = useMemo(
    () => buildings.find((b) => b.id === selectedBuildingId) || null,
    [buildings, selectedBuildingId]
  );

  // 호버된 건물 ID 계산
  const hoveredBuildingId = useMemo(() => {
    if (!hoveredCell) return null;
    const building = findBuildingByCell(hoveredCell.x, hoveredCell.z);
    return building?.id || null;
  }, [hoveredCell, findBuildingByCell]);

  return {
    // 상태
    hoveredCell,
    hoveredBuildingId, // 추가
    selectedBuildingId,
    selectedBuilding,
    modalMode,
    pendingAction,
    error,
    isLoading,

    // 데이터
    buildings, // 필터링 전 모든 건물 (도로 클러스터링용)
    placedObjects, // 필터링된 건물 (렌더링용)

    // 이벤트 핸들러
    onCellPointerOver,
    onCellPointerOut,
    onCellClick,

    // 액션
    setModalMode,
    confirmCreate,
    confirmDelete,
    closeModal,
    setSelectedBuildingId,
    refreshBuildings: loadBuildings,
  };
}
